import React, { useState, useEffect, useContext } from 'react';
import { AppContext } from 'views/App/context';
import { OrganizationTrainingConfig } from '../../_types/training';
import { useMutation, useQueryClient } from 'react-query';
import View from './components/View';
import Edit from './components/Edit';
import useTrainingSolutionConfig from './_data_sources/useTrainingSolutionConfig';
import useOrganizationTrainingConfig from './_data_sources/useOrganizationTrainingConfig';
import { updateOrganizationTrainingConfig } from './_data_sources/manageOrganizationTrainingConfig';
import { useManageSolutionsContext } from '../../context';
import SolutionCard from '../../components/SolutionCard';
import errorHelper from 'helpers/errorHelper';
import { ServerErrorResponse } from 'data_sources/constants';

const Training: React.FC = () => {
  const { selectedSolution } = useManageSolutionsContext();
  const { currentOrganizationId } = useContext(AppContext);
  const queryClient = useQueryClient();
  const [isSaveLoading, setIsSaveLoading] = useState(false);

  // Use React Query hooks for data fetching
  const { data: solutionConfig, isLoading: isLoadingSolutionConfig } = useTrainingSolutionConfig();
  const { data: organizationConfig, isLoading: isLoadingOrgConfig } = useOrganizationTrainingConfig(
    currentOrganizationId || '',
    { enabled: !!currentOrganizationId }
  );

  const updateTrainingConfigMutation = useMutation(
    async (config: OrganizationTrainingConfig) => {
      if (!currentOrganizationId) throw new Error('Missing organization id');
      return updateOrganizationTrainingConfig(currentOrganizationId, config);
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['trainingSolutionConfig']);
        if (currentOrganizationId) queryClient.invalidateQueries(['organizationTrainingConfig', currentOrganizationId]);
      },
      onError: (error: any) => {
        // Parse error and show notification
        try {
          const errorData = JSON.parse(error.message);
          errorHelper.enqueueErrors(errorData as ServerErrorResponse);
        } catch (e) {
          // Fallback if error is not JSON
          errorHelper.enqueueErrors({
            message: error.message || 'An unexpected error occurred.',
            errorDetails: null,
          } as ServerErrorResponse);
        }
      },
    }
  );

  const [localConfig, setLocalConfig] = useState<OrganizationTrainingConfig | null>(null);

  // Initialize local config when data loads
  useEffect(() => {
    if (solutionConfig && currentOrganizationId) {
      const enabledPlanIds = new Set(organizationConfig?.modules.map((m) => m.id) || []);

      // Merge solution config plans with organization config to get full list
      // organizationConfig only contains enabled subscriptions, so we need to merge with all plans
      const defaultConfig: OrganizationTrainingConfig = {
        modules: solutionConfig.plans.map((plan: { id: string; name: string }) => ({
          id: plan.id,
          name: plan.name,
          status: enabledPlanIds.has(plan.id) ? 'ENABLED' : 'DISABLED',
        })),
      };
      setLocalConfig(defaultConfig);
    }
  }, [organizationConfig, solutionConfig, currentOrganizationId]);

  const handleConfigChange = (updatedConfig: OrganizationTrainingConfig) => {
    setLocalConfig(updatedConfig);
  };

  // Handle save button click
  const handleSaveClick = async () => {
    if (!localConfig) return;
    setIsSaveLoading(true);

    try {
      await updateTrainingConfigMutation.mutateAsync(localConfig);
    } finally {
      setIsSaveLoading(false);
    }
  };

  // Handle cancel button click - reset localConfig from cached query data and invalidate queries
  const handleCancelClick = () => {
    // Reset localConfig immediately from cached query data
    const cachedSolutionConfig = queryClient.getQueryData(['trainingSolutionConfig']);
    const cachedOrgConfig = currentOrganizationId
      ? queryClient.getQueryData(['organizationTrainingConfig', currentOrganizationId])
      : null;

    if (cachedSolutionConfig && currentOrganizationId) {
      const enabledPlanIds = new Set((cachedOrgConfig as any)?.modules?.map((m: { id: string }) => m.id) || []);
      const defaultConfig: OrganizationTrainingConfig = {
        modules: (cachedSolutionConfig as any).plans.map((plan: { id: string; name: string }) => ({
          id: plan.id,
          name: plan.name,
          status: enabledPlanIds.has(plan.id) ? 'ENABLED' : 'DISABLED',
        })),
      };
      setLocalConfig(defaultConfig);
    }

    // Invalidate queries to ensure we have the latest data
    queryClient.invalidateQueries(['trainingSolutionConfig']);
    if (currentOrganizationId) {
      queryClient.invalidateQueries(['organizationTrainingConfig', currentOrganizationId]);
    }
  };

  const isLoading = isLoadingSolutionConfig || isLoadingOrgConfig;

  if (!localConfig) {
    return (
      <SolutionCard
        solutionKey={selectedSolution!.key}
        title={selectedSolution!.name}
        view={<div>Loading...</div>}
        edit={<div>Loading...</div>}
        isLoading={true}
      />
    );
  }

  return (
    <SolutionCard
      solutionKey={selectedSolution!.key}
      title={selectedSolution!.name}
      view={<View config={localConfig} />}
      edit={
        <Edit
          config={localConfig}
          onChange={handleConfigChange}
        />
      }
      onSave={handleSaveClick}
      onCancel={handleCancelClick}
      isLoading={isLoading || isSaveLoading}
    />
  );
};

export default Training;
